import { FullscreenModal, Toast } from "shared";
import { getAssetModalTitle, buildInvestmentPayload } from "../../utils";
import InvestmentForm from "./InvestmentForm";
import { InvestmentFormValues } from "./forms";
import {
  useStoreInvestment,
  useUploadInvestmentFile,
  useAttachInvestmentFile,
  useGetInvestmentsGrouped,
} from "hooks/investments";
import { useGetMe } from "hooks/auth";
import { TOAST_TYPES_ENUM } from "enums";
import "./AddInvestmentModal.scss";
import { useMemo, useState } from "react";
import { queryClient } from "App";

interface AddInvestmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedSlug: string | null;
  isAdmin: boolean;
  mode?: "add" | "edit";
  onSuccess?: (data: any) => void;
}

const AddInvestmentModal = ({
  isOpen,
  onClose,
  selectedSlug,
  isAdmin,
  mode = "add",
  onSuccess: onSuccessCallback,
}: AddInvestmentModalProps) => {
  const title = getAssetModalTitle(selectedSlug, isAdmin, mode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: storeInvestment } = useStoreInvestment();
  const { mutateAsync: uploadFile } = useUploadInvestmentFile();
  const { mutateAsync: attachFile } = useAttachInvestmentFile();
  const { data: me } = useGetMe();
  const { data: groupedInvestments } = useGetInvestmentsGrouped();
  const { data: archivedInvestments } = useGetInvestmentsGrouped(true);

  const checkIfCurrencyExists = useMemo(() => {
    return (currencyUuid: string) => {
      if (selectedSlug !== "currencies") return false;

      if (isAdmin) {
        const activeReliance = (groupedInvestments as any)?.reliance
          ?.currencies;
        if (
          activeReliance?.items?.some(
            (item: any) => item.currencyUuid === currencyUuid,
          )
        )
          return true;

        const archivedReliance = (archivedInvestments as any)?.reliance
          ?.currencies;
        if (
          archivedReliance?.items?.some(
            (item: any) => item.currencyUuid === currencyUuid,
          )
        )
          return true;

        return false;
      }

      const activeCurrencies = (groupedInvestments as any)?.own?.currencies;
      if (
        activeCurrencies?.items?.some(
          (item: any) => item.currencyUuid === currencyUuid,
        )
      )
        return true;

      const archivedCurrencies = (archivedInvestments as any)?.own?.currencies;
      if (
        archivedCurrencies?.items?.some(
          (item: any) => item.currencyUuid === currencyUuid,
        )
      )
        return true;

      return false;
    };
  }, [isAdmin, selectedSlug, groupedInvestments, archivedInvestments]);

  const checkIfCryptoExists = useMemo(() => {
    return (parentInvestmentUuid: string, currencyUuid: string) => {
      if (selectedSlug !== "crypto" || !groupedInvestments) return false;

      if (isAdmin) {
        const allRelianceItems = [
          ...((groupedInvestments as any)?.reliance?.crypto?.items ?? []),
          ...((archivedInvestments as any)?.reliance?.crypto?.items ?? []),
        ];
        return allRelianceItems.some(
          (item: any) =>
            item.uuid === parentInvestmentUuid &&
            item.currencyUuid === currencyUuid,
        );
      }

      const allItems = [
        ...((groupedInvestments as any)?.own?.crypto?.items ?? []),
        ...((archivedInvestments as any)?.own?.crypto?.items ?? []),
      ];

      return allItems.some(
        (item: any) =>
          item.uuid === parentInvestmentUuid &&
          item.currencyUuid === currencyUuid,
      );
    };
  }, [isAdmin, selectedSlug, groupedInvestments, archivedInvestments]);

  const handleSubmit = async (values: InvestmentFormValues) => {
    if (!selectedSlug) return;
    setIsSubmitting(true);
    const { contractFile, photoFiles, ...rest } = values as any;

    // Dla crypto: jeśli waluta nie istnieje jeszcze w tej nazwie tokenu,
    // czyścimy parentInvestmentUuid — backend ma utworzyć nowy portfel
    if (
      selectedSlug === "crypto" &&
      rest.parentInvestmentUuid &&
      rest.currencyUuid &&
      !checkIfCryptoExists(rest.parentInvestmentUuid, rest.currencyUuid)
    ) {
      delete rest.parentInvestmentUuid;
    }

    const payload = buildInvestmentPayload(
      rest as Record<string, unknown>,
      selectedSlug,
    );
    try {
      const data = await storeInvestment(payload as any);

      const userId: string | number =
        (me as any)?.user?.id ?? (me as any)?.id ?? "";

      const contractFiles = Array.isArray(contractFile)
        ? contractFile
        : contractFile instanceof File
        ? [contractFile]
        : [];

      if (contractFiles.length > 0 && data?.uuid) {
        const contracts = contractFiles.slice(0, 10);
        await Promise.allSettled(
          contracts.map(async (file: File) => {
            try {
              const uploaded = await uploadFile({
                file,
                type: "attachment",
                parentId: userId,
              });
              const fileUuid = Array.isArray(uploaded)
                ? uploaded[0]?.uuid
                : (uploaded as any)?.uuid;
              if (fileUuid) {
                await attachFile({
                  investmentUuid: data.uuid,
                  fileUuid,
                  role: "contract",
                });
              }
            } catch {
              // pojedynczy plik nie blokuje
            }
          }),
        );
      }

      const photoFilesArray = Array.isArray(photoFiles)
        ? photoFiles
        : photoFiles instanceof File
        ? [photoFiles]
        : [];

      if (photoFilesArray.length > 0 && data?.uuid) {
        const photos = photoFilesArray.slice(0, 10);
        await Promise.allSettled(
          photos.map(async (photo: File) => {
            try {
              const uploaded = await uploadFile({
                file: photo,
                type: "image",
                parentId: userId,
              });
              const fileUuid = Array.isArray(uploaded)
                ? uploaded[0]?.uuid
                : (uploaded as any)?.uuid;
              if (fileUuid) {
                await attachFile({
                  investmentUuid: data.uuid,
                  fileUuid,
                  role: "photo",
                });
              }
            } catch {
              // pojedyncze zdjęcie nie blokuje
            }
          }),
        );
      }

      await queryClient.refetchQueries({
        queryKey: ["investments/grouped"],
      });
      onSuccessCallback?.({
        ...data,
        currencyUuid:
          data.currencyUuid ?? (payload as any).currencyUuid ?? null,
      });
      onClose();
      Toast({
        message: "Aktywo dodane pomyślnie.",
        type: TOAST_TYPES_ENUM.SUCCESS,
        id: "store-investment",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!selectedSlug) return null;

  return (
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <div className="add-investment-modal">
        <h2 className="fullscreen-modal__header">{title}</h2>
        <InvestmentForm
          slug={selectedSlug}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isLoading={isSubmitting}
          isAdmin={isAdmin}
          submitLabel={mode === "add" ? "Potwierdź" : "Zapisz"}
          checkIfCurrencyExists={checkIfCurrencyExists}
          checkIfCryptoExists={checkIfCryptoExists}
        />
      </div>
    </FullscreenModal>
  );
};

export default AddInvestmentModal;
